import { SelectionManagerProvider } from "@prismicio/editor-fields"
import { useDebounce } from "@prismicio/editor-support/React"
import {
  AnimatedElement,
  Box,
  DefaultErrorMessage,
  dialogErrorImage,
  ErrorBoundary,
  Toast
} from "@prismicio/editor-ui"
import { type ReactNode, Suspense, useState, useTransition } from "react"

import { ActionBar } from "./ActionBar"
import { CreateNewDocumentModal } from "./CreateNewDocumentDialog"
import {
  DocumentListLoadingTable,
  DocumentListTable
} from "./DocumentListTable"

interface DocumentListBodyProps {
  tab: "working" | "archived" | "upcoming"
  /**
   * The release id to filter the documents by.
   * If not provided, the documents will be filtered by the current release.
   *
   * This also determines if the create new document button should link the new document to the release.
   *
   * Only works for the "upcoming" tab.
   */
  releaseId?: string
}

export function DocumentListBody(props: DocumentListBodyProps) {
  const { tab, releaseId } = props

  const [isLoadingPage, startTransition] = useTransition()

  const [debouncedIsLoading, setDebouncedIsLoading] = useState<boolean>(false)

  const [modalOpen, setModalOpen] = useState(false)

  useDebounce(isLoadingPage, 100, () => setDebouncedIsLoading(isLoadingPage))

  return (
    <>
      <ActionBar tab={tab} setModalOpen={setModalOpen} />

      <AnimatedTable tab={tab}>
        <ErrorBoundary renderError={renderDocumentListError}>
          <Suspense fallback={<DocumentListLoadingTable />}>
            <SelectionManagerProvider>
              <DocumentListTable
                startTransition={startTransition}
                tab={tab}
                releaseId={releaseId}
                setModalOpen={setModalOpen}
              />
            </SelectionManagerProvider>
          </Suspense>
        </ErrorBoundary>
      </AnimatedTable>

      <LoadingToast debouncedIsLoading={debouncedIsLoading} />
      <CreateNewDocumentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        {...(tab === "upcoming" && releaseId ? { releaseId } : {})}
      />
    </>
  )
}

interface DocumentListBodyLayoutProps {
  children: ReactNode
}

export function DocumentListBodyLayout(props: DocumentListBodyLayoutProps) {
  const { children } = props

  return (
    <Box
      flexDirection="column"
      gap={24}
      padding={{ left: 24, top: 24, bottom: 24 }}
      width="100%"
    >
      {children}
    </Box>
  )
}

interface LoadingToastProps {
  debouncedIsLoading: boolean
}

function LoadingToast(props: LoadingToastProps) {
  return (
    <Toast
      anchor={
        <Box
          position="relative"
          width="100%"
          justifyContent="center"
          bottom={72}
        />
      }
      loadingIndicator
      open={props.debouncedIsLoading}
      title="Loading more results ..."
      seconds={1}
    />
  )
}

interface AnimatedTableProps {
  tab: "working" | "archived" | "upcoming"
  children: ReactNode
}

function AnimatedTable(props: AnimatedTableProps) {
  const { tab, children } = props

  return (
    <AnimatedElement
      animation={{ name: "slide", offset: -16 }}
      enterDuration={250}
      exitDuration={150}
    >
      <Box key={tab}>{children}</Box>
    </AnimatedElement>
  )
}

function renderDocumentListError() {
  return (
    <Box
      height="100%"
      width="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <DefaultErrorMessage
        title="Request failed"
        description="An error occurred while fetching documents."
        errorImageSrc={dialogErrorImage}
      />
    </Box>
  )
}
